import { useXRPLContext } from '@/src/contexts';
import { convertCurrencyCodeToHex } from '@/src/helpers';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  AccountInfoRequest,
  AccountInfoResponse,
  AccountLinesRequest,
  AccountLinesResponse,
  AccountOffersRequest,
  AccountOffersResponse,
  AccountTxRequest,
  AccountTxResponse,
  BookOffersRequest,
  BookOffersResponse,
  GetAggregatePriceRequest,
  GetAggregatePriceResponse,
  SubscribeRequest,
  SubscribeResponse,
  UnsubscribeRequest,
  UnsubscribeResponse,
} from 'xrpl';
import { RequestError } from '../fireblocks/fireblocks.types';
import { isXRPLErrorCode, isXRPLErrorResponse, KnownXrplErrorCodes } from './xrpl.helpers';
import { XrplQueryKeys, BookChangesResponse } from './xrpl.types';

type GetAggregatePriceParams = Pick<
  GetAggregatePriceRequest,
  'base_asset' | 'quote_asset' | 'oracles'
>;

/**
 * Get the aggregate price of a given asset from the XRPL
 * @param params - The parameters for the request
 * @returns The aggregate price of the asset
 */
export const useGetAggregatePrice = ({
  base_asset,
  quote_asset,
  oracles,
}: GetAggregatePriceParams) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.GetAggregatePrice, base_asset, quote_asset, oracles],
    queryFn: async () => {
      return client
        .request({
          command: XrplQueryKeys.GetAggregatePrice,
          base_asset: convertCurrencyCodeToHex(base_asset),
          quote_asset: convertCurrencyCodeToHex(quote_asset),
          oracles,
        })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (
              isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams) ||
              isXRPLErrorCode(err.data, KnownXrplErrorCodes.ObjectNotFound) ||
              isXRPLErrorCode(err.data, KnownXrplErrorCodes.OracleMalformed)
            ) {
              return {} as GetAggregatePriceResponse['result'];
            }
          }
          throw err;
        });
    },
  });
};

// Account Info

type AccountInfoParams = Pick<AccountInfoRequest, 'account' | 'queue' | 'signer_lists' | 'strict'>;

export const useAccountInfo = (
  params: AccountInfoParams,
  opts?: Omit<UseQueryOptions<AccountInfoResponse['result'], RequestError>, 'queryKey' | 'queryFn'>
) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.AccountInfo, params.account, client.url],
    queryFn: () =>
      client
        .request({
          ...params,
          command: XrplQueryKeys.AccountInfo,
        })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.AccountNotFound)) {
              return {} as AccountInfoResponse['result'];
            }
          }
          throw err;
        }),
    ...opts,
  });
};

// Account Lines

type AccountLinesParams = Pick<AccountLinesRequest, 'account' | 'limit' | 'marker' | 'peer'>;

export const useAccountLines = (
  params: AccountLinesParams,
  opts?: Omit<UseQueryOptions<AccountLinesResponse['result'], RequestError>, 'queryKey' | 'queryFn'>
) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.AccountLines, params.account, client.url],
    queryFn: () =>
      client
        .request({
          ...params,
          command: XrplQueryKeys.AccountLines,
        })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (
              isXRPLErrorCode(err.data, KnownXrplErrorCodes.AccountNotFound) ||
              isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)
            ) {
              return {} as AccountLinesResponse['result'];
            }
          }
          throw err;
        }),
    ...opts,
  });
};

// Subscription methods

type SubscribeParams = Pick<
  SubscribeRequest,
  'streams' | 'books' | 'accounts' | 'accounts_proposed' | 'url' | 'url_username' | 'url_password'
>;

export const useSubscribe = (params?: SubscribeParams) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.Subscribe, params],
    queryFn: () =>
      client
        .request({ ...params, command: XrplQueryKeys.Subscribe })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)) {
              return {} as SubscribeResponse['result'];
            }
          }
          throw err;
        }),
  });
};

type UnsubscribeParams = Pick<
  UnsubscribeRequest,
  'streams' | 'accounts' | 'accounts_proposed' | 'books'
>;

export const useUnsubscribe = (params?: UnsubscribeParams) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.Unsubscribe, params],
    queryFn: () =>
      client
        .request({ ...params, command: XrplQueryKeys.Unsubscribe })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)) {
              return {} as UnsubscribeResponse['result'];
            }
          }
          throw err;
        }),
  });
};

// Book Offers
export const useBookOffers = (
  params: BookOffersRequest,
  opts?: Omit<UseQueryOptions<BookOffersResponse['result'], RequestError>, 'queryKey' | 'queryFn'>
) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.BookOffers, params],
    queryFn: () =>
      client
        .request({ ...params, command: XrplQueryKeys.BookOffers })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)) {
              return {} as BookOffersResponse['result'];
            }
          }
          throw err.data;
        }),
    ...opts,
  });
};

// Account Offers
export const useAccountOffers = (
  params: AccountOffersRequest,
  opts?: Omit<
    UseQueryOptions<AccountOffersResponse['result'], RequestError>,
    'queryKey' | 'queryFn'
  >
) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.AccountOffers, params],
    queryFn: () =>
      client
        .request({ ...params, command: XrplQueryKeys.AccountOffers })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)) {
              return {} as AccountOffersResponse['result'];
            }
          }
          throw err.data;
        }),
    ...opts,
  });
};

/**
 * Get account transaction history from the XRPL
 * @param params - The parameters for the request
 * @param opts - Additional query options
 * @returns The account transaction history
 */
export const useAccountTx = (
  params: AccountTxRequest,
  opts?: Omit<UseQueryOptions<AccountTxResponse['result'], RequestError>, 'queryKey' | 'queryFn'>
) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.AccountTx, params],
    queryFn: () =>
      client
        .request({ ...params, command: XrplQueryKeys.AccountTx })
        .then((res) => res.result)
        .catch((err) => {
          if (isXRPLErrorResponse(err)) {
            if (isXRPLErrorCode(err.data, KnownXrplErrorCodes.InvalidParams)) {
              return {} as AccountTxResponse['result'];
            }
          }
          throw err.data;
        }),
    ...opts,
  });
};

// Book Changes (for candlestick charts)

interface BookChangesParams {
  baseSymbol: string | undefined;
  baseIssuer?: string;
  quoteSymbol?: string;
  quoteIssuer?: string;
  days?: number;
  enabled?: boolean;
}

interface CandlestickData {
  time: number;
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  isBullish: boolean;
  bodyLow: number;
  bodyHigh: number;
  bodyHeight: number;
}

interface DailyData {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  timestamp: number;
}

/**
 * Helper function to format currency identifier for book_changes
 * XRP is formatted as "XRP_drops"
 * Tokens are formatted as "issuer/currencyCode"
 */
function formatCurrencyId(symbol: string, issuer?: string): string {
  if (symbol === 'XRP') {
    return 'XRP_drops';
  }
  return `${issuer}/${symbol}`;
}

/**
 * Get daily candlestick data from XRPL book_changes
 * Samples ledgers across each day and aggregates into daily OHLC candles
 */
export const useBookChanges = ({
  baseSymbol,
  baseIssuer,
  quoteSymbol = 'XRP',
  quoteIssuer,
  days = 20,
  enabled = true,
}: BookChangesParams) => {
  const { client } = useXRPLContext();

  return useQuery({
    queryKey: [XrplQueryKeys.BookChanges, baseSymbol, baseIssuer, quoteSymbol, quoteIssuer, days],
    queryFn: async () => {
      if (!baseSymbol) {
        throw new Error('Base symbol is required');
      }

      // Format currency identifiers for matching
      const baseCurrencyId = formatCurrencyId(baseSymbol, baseIssuer);
      const quoteCurrencyId = formatCurrencyId(quoteSymbol, quoteIssuer);

      // Get current ledger index
      const serverInfo = await client.request({ command: 'server_info' });
      const currentLedger = serverInfo.result.info.validated_ledger?.seq;

      if (!currentLedger) {
        throw new Error('Could not get current ledger index');
      }

      // XRPL ledgers close ~every 3-4 seconds, so ~21,600 ledgers per day
      const LEDGERS_PER_DAY = 21600;
      const SAMPLES_PER_DAY = 24; // Sample every hour for better coverage

      // Array to store all ledger data with timestamps
      interface LedgerData {
        dayKey: string;
        timestamp: number;
        open: number;
        high: number;
        low: number;
        close: number;
        volume: number;
      }
      const allLedgerData: LedgerData[] = [];

      // Query ledgers across the past N days
      const ledgerPromises: Promise<BookChangesResponse>[] = [];

      for (let dayOffset = 0; dayOffset < days; dayOffset++) {
        const dayStartLedger = currentLedger - (dayOffset * LEDGERS_PER_DAY);

        // Sample ledgers throughout this day
        for (let sample = 0; sample < SAMPLES_PER_DAY; sample++) {
          const ledgerIndex = dayStartLedger - Math.floor((sample * LEDGERS_PER_DAY) / SAMPLES_PER_DAY);

          ledgerPromises.push(
            client.request({
              command: XrplQueryKeys.BookChanges as any,
              ledger_index: ledgerIndex,
            }) as Promise<BookChangesResponse>
          );
        }
      }

      const ledgerResults = await Promise.allSettled(ledgerPromises);

      // Process each ledger result and collect all data
      ledgerResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.result.changes) {
          const { ledger_time, changes } = result.value.result;

          // Find matching currency pair in this ledger
          const match = changes.find((change) => {
            return (
              (change.currency_a === baseCurrencyId && change.currency_b === quoteCurrencyId) ||
              (change.currency_a === quoteCurrencyId && change.currency_b === baseCurrencyId)
            );
          });

          if (match) {
            const open = parseFloat(match.open);
            const high = parseFloat(match.high);
            const low = parseFloat(match.low);
            const close = parseFloat(match.close);
            const volume = parseFloat(match.volume_a);

            // If the match is in reverse order, invert the prices
            const isReversed = match.currency_a === quoteCurrencyId;
            const adjustPrice = (price: number) => (isReversed && price !== 0 ? 1 / price : price);

            const adjustedOpen = adjustPrice(open);
            const adjustedClose = adjustPrice(close);
            const adjustedHigh = adjustPrice(isReversed ? low : high);
            const adjustedLow = adjustPrice(isReversed ? high : low);

            // Get the day key (YYYY-MM-DD)
            const dayKey = new Date(ledger_time * 1000).toISOString().split('T')[0];

            allLedgerData.push({
              dayKey,
              timestamp: ledger_time,
              open: adjustedOpen,
              high: adjustedHigh,
              low: adjustedLow,
              close: adjustedClose,
              volume,
            });
          }
        }
      });

      // Sort by timestamp to ensure proper order
      allLedgerData.sort((a, b) => a.timestamp - b.timestamp);

      // Aggregate into daily candles
      const dailyDataMap = new Map<string, DailyData>();

      allLedgerData.forEach((data) => {
        const { dayKey, timestamp, open, high, low, close, volume } = data;

        if (!dailyDataMap.has(dayKey)) {
          // First data point for this day - use its open as daily open
          dailyDataMap.set(dayKey, {
            open,
            high,
            low,
            close,
            volume,
            timestamp,
          });
        } else {
          const dailyData = dailyDataMap.get(dayKey)!;
          // Don't update open (keep first open of the day)
          dailyData.high = Math.max(dailyData.high, high);
          dailyData.low = Math.min(dailyData.low, low);
          dailyData.close = close; // Update to latest close
          dailyData.volume += volume;
          dailyData.timestamp = timestamp; // Update to latest timestamp
        }
      });

      // Convert to candlestick array
      const candlesticks: CandlestickData[] = Array.from(dailyDataMap.entries()).map(([dayKey, data]) => {
        const isBullish = data.close >= data.open;

        return {
          time: data.timestamp,
          date: new Date(dayKey).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
          volume: data.volume,
          isBullish,
          bodyLow: Math.min(data.open, data.close),
          bodyHigh: Math.max(data.open, data.close),
          bodyHeight: Math.abs(data.close - data.open),
        };
      });

      // Sort by time (oldest first)
      candlesticks.sort((a, b) => a.time - b.time);

      return candlesticks;
    },
    enabled: enabled && !!baseSymbol && (baseSymbol === 'XRP' || !!baseIssuer),
    staleTime: 5 * 60 * 1000, // 5 minutes - daily data doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
  });
};
